import React from "react";
import { Request, RequestType } from "@/types/request";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ViewModes } from "@/components/pages/requests/RequestsPage";
import {
  RequestColumnKey,
  RequestsTable,
} from "@/components/pages/requests/components/RequestsTable";
import { useTranslation } from "react-i18next";
import { CheckboxSelectionResult } from "@/hooks/useCheckboxSelection";
import { RequestsKanban } from "@/components/pages/requests/components/kanban/RequestsKanban";

const requestsViewColumns: RequestColumnKey[] = [
  "select",
  "priority",
  "guest.profile",
  "booking",
  "service",
  "createdAt",
  "dueDateTime",
  "status",
  "assignedStaff",
  "createdBy",
  "paymentStatus",
  "actions",
];

export const RequestsView: React.FC<{
  requests: Request[];
  activeTab: RequestType;
  viewMode: ViewModes;
  currentPage: number;
  totalPages: number;
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
  sortField?: string | null;
  sortDirection?: "asc" | "desc" | null;
  onUpdateRequest?: (requestId: string, updatedData: Partial<Request>) => void;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  onFiltersChange?: (filters: {
    status?: string[];
    serviceName?: string[];
    priority?: boolean;
  }) => void;
  selection: CheckboxSelectionResult;
  roomCategoryById?: Record<string, { name: string; pmsType?: string }>;
}> = ({
  activeTab,
  viewMode,
  requests,
  sortField,
  sortDirection,
  currentPage,
  totalPages,
  onSort,
  onPageChange,
  onUpdateRequest,
  onEditRequest,
  selection,
  roomCategoryById,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = false;

  const handleUpdateRequest = async (
    requestId: string,
    updatedData: Partial<Request>,
  ) => {
    try {
      onUpdateRequest?.(requestId, updatedData);

      if (updatedData.priority !== undefined) {
        toast.success(
          `Request ${updatedData.priority ? t("requests.messages.markedAsPriority") : t("requests.messages.priorityRemoved")}`,
        );
      } else {
        toast.success(t("requests.messages.requestUpdatedSuccessfully"));
      }
    } catch (error) {
      console.error(t("requests.form.failedToUpdateRequest"), error);
      toast.error(t("requests.form.failedToUpdateRequest"));
    }
  };

  return (
    <>
      {viewMode === ViewModes.LIST && (
        <RequestsTable
          requests={requests}
          visibleColumns={requestsViewColumns}
          activeTab={activeTab}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          sortField={sortField}
          sortDirection={sortDirection}
          onPageChange={onPageChange}
          onSort={onSort}
          onRowClick={(row: Request) =>
            navigate({
              pathname: `/requests/${row.id}`,
              search: location.search,
            })
          }
          onUpdateRequest={handleUpdateRequest}
          onEditRequest={onEditRequest}
          selection={selection}
        />
      )}
      {viewMode === ViewModes.KANBAN && (
        <RequestsKanban
          requests={requests}
          onRequestClick={(id) =>
            navigate({
              pathname: `/requests/${id}`,
              search: location.search,
            })
          }
          onUpdateRequest={handleUpdateRequest}
          onEditRequest={onEditRequest}
          roomCategoryById={roomCategoryById}
        />
      )}
    </>
  );
};
